/**
 * AdminIonEvalCasesSubtab — a READ-ONLY view of the Ion eval corpus.
 *
 * The corpus lives in the repository at `backend/app/api/v1/chat/eval/cases/cases.json`
 * and is deployed with the code. It was a `ion_eval_cases` table edited
 * through this screen, with an author/approver proposal queue; all of that is
 * gone, along with the table.
 *
 * WHY, so this screen does not read as a regression: these are the fixtures
 * that gate Ion's quality — test code. Editing them in a database meant no
 * diff, no review, no rollback, and silent drift between environments
 * (production held a stale subset of 38 of staging's 92 cases when it was
 * measured). Changing a case is now a merge request, which is also where the
 * decision that no verbatim customer prompt enters the repository gets made
 * and reviewed.
 */
import { useEffect, useMemo, useState } from 'preact/hooks';
import { listIonEvalCases } from '../../services/adminService.js';

const CORPUS_PATH = 'backend/app/api/v1/chat/eval/cases/cases.json';

export function AdminIonEvalCasesSubtab() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [suite, setSuite] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  // No export/import here: the corpus is one file in one repository, so there
  // is no bundle to download or promote. To see or diff it, read the file in
  // git; to change it, open a merge request against it.

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listIonEvalCases({
        search,
        suite,
        active_only: activeOnly ? 'true' : '',
      });
      setItems(data.cases || []);
      setTotal(data.total || 0);
    } catch (e) {
      setError(e?.message || 'network');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [search, suite, activeOnly]);

  const suites = useMemo(
    () => [...new Set(items.map((c) => c.suite).filter(Boolean))].sort(),
    [items],
  );

  return (
    <div className="admin-aim-subtab-body">
      <header className="admin-aim-header">
        <div>
          <h2 className="admin-aim-title">Eval cases</h2>
          <p className="admin-aim-sub">
            The corpus the run configurator uses. Read-only here — cases live in
            the repository at <code>{CORPUS_PATH}</code> and are changed in a
            merge request, so every edit has a diff, a review and a rollback.
          </p>
        </div>
        <div className="admin-aim-header-actions">
          <button type="button" className="admin-ai-btn admin-ai-btn--ghost" onClick={load}>↻</button>
        </div>
      </header>

      <div className="admin-aim-field-grid" style={{ marginBottom: 12 }}>
        <input
          type="text"
          placeholder="Search case name or prompt…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select value={suite} onChange={(e) => setSuite(e.target.value)}>
          <option value="">All suites</option>
          {suites.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <label>
          <input
            type="checkbox"
            checked={activeOnly}
            onChange={(e) => setActiveOnly(e.target.checked)}
          /> Active only
        </label>
      </div>

      {error && <div className="admin-aim-error-banner">{error}</div>}

      <div className="admin-aim-table-wrap">
        <table className="admin-aim-table">
          <thead>
            <tr>
              <th>Case name</th>
              <th>Prompt</th>
              <th>Suite</th>
              <th>Confidence</th>
              <th>Active</th>
            </tr>
          </thead>
          <tbody>
            {loading && items.length === 0 ? (
              <tr><td colSpan={5} className="admin-aim-empty">Loading…</td></tr>
            ) : items.length === 0 ? (
              <tr><td colSpan={5} className="admin-aim-empty">No eval cases match.</td></tr>
            ) : items.map((c) => (
              /* Keyed by caseName — the natural key. The corpus is a file and
                 carries no integer id. */
              <tr key={c.caseName}>
                <td>{c.caseName}</td>
                <td style={{ maxWidth: 360, whiteSpace: 'normal' }}>{c.prompt}</td>
                <td>{c.suite || '—'}</td>
                <td>{c.confidence}</td>
                <td>{c.isActive ? '✓' : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="admin-aim-meta">{total} case(s) total</div>
    </div>
  );
}
